import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumbs from '../../components/Breadcrumbs';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { ArrowRight, CheckCircle, Cpu } from 'lucide-react';

const Electronics = () => {
  return (
    <div>
      <SEO 
        title="Electronics & Semiconductor Trade Compliance Services"
        description="Expert EAR compliance for electronics, semiconductors, telecommunications equipment, and computing technology. Navigate export controls for dual-use technology."
        keywords="electronics export controls, semiconductor compliance, EAR electronics, dual-use technology, chip export regulations"
        path="/industries/electronics"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Services', path: '/services' },
        { label: 'Electronics & Semiconductor', path: '/industries/electronics' }
      ]} />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Electronics & Semiconductor Trade Compliance
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              The electronics and semiconductor industry faces complex dual-use export controls. Our specialized compliance services help you navigate EAR requirements for computing technology, telecommunications equipment, and integrated circuits.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Electronics Export Control Challenges</h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-purple-50 rounded-lg p-8">
              <Cpu className="h-10 w-10 text-purple-700 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">ECCN 3A001 & 4A003</h3>
              <p className="text-gray-700">Computing and telecommunications equipment often fall under tightly controlled ECCNs with significant licensing requirements.</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Encryption Controls</h3>
              <p className="text-gray-700">Products with encryption capabilities face special reporting and licensing requirements under ENC provisions.</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Entity List Screening</h3>
              <p className="text-gray-700">Semiconductor industry faces heightened scrutiny with extensive Entity List restrictions for certain foreign entities.</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Technology Transfer</h3>
              <p className="text-gray-700">Design files, source code, and technical specifications require careful deemed export control.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Electronics Compliance Services</h2>
          
          <div className="max-w-4xl mx-auto space-y-6">
            {[
              'ECCN classification for electronics and semiconductor products',
              'Encryption item classification and reporting (ENC)',
              'Entity List and denied party screening',
              'License applications for restricted destinations',
              'Technology transfer controls for design data',
              'Supply chain compliance for components',
              'De minimis calculations for foreign-made items',
              'Deemed export controls for R&D operations'
            ].map((service, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-lg text-gray-800">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Electronics Export Compliance Consultation
          </h2>
          <p className="text-xl text-slate-100 mb-10 max-w-2xl mx-auto">
            Navigate complex EAR requirements for electronics and semiconductor products with expert guidance.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Electronics;